// ---------------------------------------------------------------------------
// Game state model + pure container ("functional group") logic.
//
// The world is a flat table of GameObjects. Containers (decks, chip stacks)
// hold their contents as an ordered list of lightweight Items — contained
// things do NOT exist as world objects until pulled out. This module is pure
// so the server can be authoritative and tests can exercise the rules.
//
// Contents order: index 0 = BOTTOM … last = TOP. Draw/pull pops the top.
// ---------------------------------------------------------------------------

import { getDef, CARD_THICKNESS, type ObjectDef } from "./defs";
import { pointInRectXZ, type V3 } from "./math";

export type PlayerId = string;

/** A serialized object inside a container. Deliberately tiny. */
export interface Item {
  def: string;
  name?: string;
}

export interface GameObject {
  id: string;
  def: string;
  pos: V3;
  yaw: number;          // rotation about vertical axis, radians
  faceUp: boolean;
  locked?: boolean;     // immovable (boards, fixed setup)
  heldBy?: PlayerId | null;
  /** Container contents — SERVER ONLY. Never serialized to clients. */
  contents?: Item[];
  /** Public container info (derived from contents when broadcasting). */
  count?: number;
  /** Derived at serialization: def of the visible top item of a container. */
  topDef?: string;
  /** Zone id this object currently sits in (hand zones drive redaction). */
  inZone?: string | null;
  /** Zones only: which seat owns this hand zone. */
  seat?: number;
  /** Zones only: current width (grows with the cards inside, capped). */
  zw?: number;
  /** Slot zones: which pile this slot holds (commander/trick/TCG tables). */
  slot?: SlotKind;
  /** MTG-style counters sitting on this card. */
  counters?: Array<{ id: string; label: string; n: number }>;
  /** Dice: the face currently showing (1..sides). Set by server rolls. */
  value?: number;
  name?: string;
}

export type SlotKind =
  | "library" | "graveyard" | "exile" | "command"   // MTG mats
  | "trick"                                          // trick-taking mat
  | "active" | "bench" | "prize"                     // Pokémon mat
  | "inkwell";                                       // Lorcana mat

/** Slots whose piles sit face-DOWN (decks, prizes, ink). The rest face up. */
const FACE_DOWN_SLOTS: ReadonlySet<string> = new Set(["library", "prize", "inkwell"]);

/** Facing rule for a card landing on a slot zone. */
export const slotWantsFaceUp = (slot: SlotKind | undefined): boolean =>
  !FACE_DOWN_SLOTS.has(slot ?? "");

export interface Player {
  id: PlayerId;
  name: string;
  color: string;
  seat: number;
  connected: boolean;
}

export interface RoomState {
  objects: Map<string, GameObject>;
  nextId: number;
}

export const TABLE = { w: 170, d: 110 } as const;
export const HOVER_HEIGHT = 3.2;

export const SEAT_COLORS = ["#e5533d", "#3d8fe5", "#3dc76a", "#e5b53d", "#a45de0", "#e07bb1"];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export const objDef = (o: GameObject): ObjectDef => getDef(o.def);

/** Height (y extent) of an object as it currently exists in the world. */
export function objHeight(o: GameObject): number {
  const d = objDef(o);
  if (d.kind === "stack") {
    const per = d.acceptsGroup?.startsWith("cards") ? CARD_THICKNESS : d.size[1];
    return Math.max(1, o.count ?? o.contents?.length ?? 1) * per;
  }
  return d.size[1];
}

/** Resting y for an object sitting on the table (pos.y is the object center). */
export const restY = (o: GameObject): number => objHeight(o) / 2;

export function isContainer(o: GameObject): boolean {
  return objDef(o).kind === "stack";
}

export function stackGroupOf(o: GameObject): string | undefined {
  const d = objDef(o);
  return d.kind === "stack" ? d.acceptsGroup : d.stackGroup;
}

/** Can `held` be merged onto `target`? (same stacking group, both stackable) */
export function canMerge(held: GameObject, target: GameObject): boolean {
  if (held.id === target.id || target.locked) return false;
  const g1 = stackGroupOf(held), g2 = stackGroupOf(target);
  return !!g1 && g1 === g2;
}

/** Serialize a world object into a container Item. */
export function toItem(o: GameObject): Item {
  const it: Item = { def: o.def };
  if (o.name) it.name = o.name;
  return it;
}

/**
 * Merge `held` into/onto `target`, mutating `target` in place.
 * If target is a lone stackable object it is promoted to a container.
 * Returns the container object (=== target). Caller removes `held` from world.
 * A held container dropped onto a target pours all of its contents on top.
 */
export function mergeInto(held: GameObject, target: GameObject): GameObject {
  // The pile may be resting on a mat or board, not the bare table: keep its
  // bottom face where it is and let the pile grow upward as contents arrive.
  const base = target.pos.y - objHeight(target) / 2;
  if (!isContainer(target)) {
    // Promote lone object → container holding itself.
    const d = objDef(target);
    target.contents = [toItem(target)];
    target.def = d.stackDef!;
    target.name = undefined;
  }
  const contents = target.contents!;
  if (isContainer(held)) {
    contents.push(...(held.contents ?? []));
  } else {
    contents.push(toItem(held));
  }
  target.count = contents.length;
  target.pos.y = Math.max(restY(target), base + objHeight(target) / 2);
  return target;
}

/**
 * Pull the TOP item out of a container. Mutates the container.
 * Returns the popped Item, or null if empty.
 * NOTE: when the container drops to 1 item the caller should collapse it
 * via `collapseIfSingle`.
 */
export function popTop(container: GameObject): Item | null {
  const c = container.contents;
  if (!c || c.length === 0) return null;
  const item = c.pop()!;
  container.count = c.length;
  container.pos.y = restY(container);
  return item;
}

/**
 * TTS behavior: a deck with one card left becomes that card. Mutates the
 * container object in place into the lone item (keeping id/pos/yaw/facing).
 * Returns true if a collapse happened.
 */
export function collapseIfSingle(container: GameObject): boolean {
  const c = container.contents;
  if (!c || c.length !== 1) return false;
  const item = c[0]!;
  container.def = item.def;
  container.name = item.name;
  container.contents = undefined;
  container.count = undefined;
  container.pos.y = restY(container);
  return true;
}

/** Materialize an Item as a fresh world object (id assigned by caller). */
export function materialize(item: Item, id: string, pos: V3, yaw: number, faceUp: boolean): GameObject {
  const o: GameObject = { id, def: item.def, pos: { ...pos }, yaw, faceUp };
  if (item.name) o.name = item.name;
  return o;
}

/** Flip an object; containers reverse their contents (like flipping a deck). */
export function flipObject(o: GameObject): void {
  o.faceUp = !o.faceUp;
  if (o.contents) o.contents.reverse();
}

// ---------------------------------------------------------------------------
// Spatial queries
// ---------------------------------------------------------------------------

/** Is a world point inside a zone object (rotated rect on XZ)? */
export function inZone(zone: GameObject, x: number, z: number): boolean {
  const d = objDef(zone);
  return pointInRectXZ(x, z, zone.pos.x, zone.pos.z, zone.zw ?? d.size[0], d.size[2], zone.yaw);
}

/**
 * Find the best drop target under (x, z): the topmost non-locked, non-held
 * object whose footprint contains the point and which `held` can merge with.
 */
export function findMergeTarget(
  objects: Iterable<GameObject>,
  held: GameObject,
  x: number,
  z: number,
): GameObject | null {
  let best: GameObject | null = null;
  for (const o of objects) {
    if (o.id === held.id || o.heldBy || objDef(o).kind === "zone") continue;
    // Cards resting in a hand zone never merge — hands hold loose cards only.
    if (o.inZone) continue;
    if (!canMerge(held, o)) continue;
    const d = objDef(o);
    // Generous hit area (110%) so drops "snap" satisfyingly like TTS.
    if (!pointInRectXZ(x, z, o.pos.x, o.pos.z, d.size[0] * 1.1, d.size[2] * 1.1, o.yaw)) continue;
    if (!best || o.pos.y > best.pos.y) best = o;
  }
  return best;
}

/**
 * Height of the tallest resting surface under (x, z) — 0 for bare table.
 * Held objects are ignored. Used to float a dragged object ABOVE whatever
 * it is currently over (decks, boards, other cards) instead of through it.
 */
export function supportHeightAt(objects: Iterable<GameObject>, x: number, z: number): number {
  let top = 0;
  for (const o of objects) {
    if (o.heldBy) continue;
    const d = objDef(o);
    if (d.kind === "zone") continue;
    if (!pointInRectXZ(x, z, o.pos.x, o.pos.z, d.size[0] * 1.05, d.size[2] * 1.05, o.yaw)) continue;
    top = Math.max(top, o.pos.y + objHeight(o) / 2);
  }
  return top;
}

/**
 * Resting center-y for `o` released at (x, z): on top of whatever solid
 * object is under the point (board, deck, another piece), else the table.
 * Excludes `o` itself and anything held.
 */
export function settleY(objects: Iterable<GameObject>, o: GameObject, x: number, z: number): number {
  let top = 0;
  for (const q of objects) {
    if (q.id === o.id || q.heldBy) continue;
    const d = objDef(q);
    if (d.kind === "zone") continue;
    if (!pointInRectXZ(x, z, q.pos.x, q.pos.z, d.size[0], d.size[2], q.yaw)) continue;
    top = Math.max(top, q.pos.y + objHeight(q) / 2);
  }
  return top + objHeight(o) / 2;
}

/**
 * Hover height for a held object at (x, z), riding above whatever is there.
 * `held` (and anything in `exclude`) never counts as its own support — the
 * client predicts this before the server's heldBy flag round-trips, and a
 * self-supporting object climbs by its own height every recompute.
 */
export function hoverYAt(
  objects: Iterable<GameObject>,
  held: GameObject,
  x: number,
  z: number,
  exclude?: ReadonlySet<string>,
): number {
  let top = 0;
  for (const q of objects) {
    if (q.id === held.id || q.heldBy || exclude?.has(q.id)) continue;
    const d = objDef(q);
    if (d.kind === "zone") continue;
    if (!pointInRectXZ(x, z, q.pos.x, q.pos.z, d.size[0] * 1.05, d.size[2] * 1.05, q.yaw)) continue;
    top = Math.max(top, q.pos.y + objHeight(q) / 2);
  }
  return top + HOVER_HEIGHT + objHeight(held) / 2;
}

/** Find the hand zone containing a point, if any (slots are not hands). */
export function findHandZone(objects: Iterable<GameObject>, x: number, z: number): GameObject | null {
  for (const o of objects) {
    if (o.def !== "zone.hand") continue;
    if (inZone(o, x, z)) return o;
  }
  return null;
}

/** Find the playmat slot zone containing a point, if any. */
export function findSlotZone(objects: Iterable<GameObject>, x: number, z: number): GameObject | null {
  for (const o of objects) {
    if (o.def !== "zone.slot") continue;
    if (inZone(o, x, z)) return o;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Hand layout — server tidies cards inside hand zones into a fan/row.
// ---------------------------------------------------------------------------

export function handSlotPosition(zone: GameObject, index: number, total: number): { x: number; z: number; yaw: number } {
  const d = getDef(zone.def);
  const usable = (zone.zw ?? d.size[0]) - 8;
  const spacing = total > 1 ? Math.min(7, usable / (total - 1)) : 0;
  const startX = -((total - 1) * spacing) / 2;
  const lx = startX + index * spacing;
  // Transform from zone-local to world.
  const cos = Math.cos(zone.yaw), sin = Math.sin(zone.yaw);
  return {
    x: zone.pos.x + lx * cos,
    z: zone.pos.z + lx * sin,
    yaw: zone.yaw,
  };
}
